import { useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import type { FilterSize } from './filterSize';
import { DEFAULT_FILTER_SIZE } from './filterSize';
import { getCountedLabel } from './filterLabel';

interface OptionEntry<T extends string> {
  value: T;
  label: string;
}

interface CommonProps<T extends string> {
  label: string;
  options: readonly (T | OptionEntry<T>)[];
  placeholder?: string;
  minWidth?: number;
  size?: FilterSize;
  /**
   * When true, the input value displays "Label (1)" instead of the selected
   * option's label. Use for filter-style selects (e.g. Customer).
   * Defaults to false — the input shows the selected option's label normally.
   */
  showCount?: boolean;
}

interface ClearableProps<T extends string> extends CommonProps<T> {
  clearable: true;
  value: T | null;
  onChange: (val: T | null) => void;
}

interface RequiredProps<T extends string> extends CommonProps<T> {
  clearable?: false;
  value: T;
  onChange: (val: T) => void;
  /**
   * When provided, the label suffix `(1)` only appears when `value !== defaultValue`.
   * If omitted, a non-null required value is always considered "selected".
   */
  defaultValue?: T;
}

type SingleAutocompleteFilterProps<T extends string> =
  | ClearableProps<T>
  | RequiredProps<T>;

const isOptionEntry = <T extends string>(
  opt: T | OptionEntry<T>,
): opt is OptionEntry<T> =>
  typeof opt === 'object' && opt !== null && 'value' in opt;

const SingleAutocompleteFilter = <T extends string>(
  props: SingleAutocompleteFilterProps<T>,
) => {
  const {
    label,
    options,
    placeholder = 'Search...',
    minWidth = 150,
    size = DEFAULT_FILTER_SIZE,
    showCount = false,
  } = props;

  const normalized: OptionEntry<T>[] = options.map((opt) =>
    isOptionEntry(opt) ? opt : { value: opt, label: opt },
  );

  const selected =
    props.value !== null && props.value !== undefined
      ? normalized.find((o) => o.value === props.value) ?? null
      : null;

  const hasSelection = props.clearable
    ? props.value !== null
    : props.defaultValue !== undefined
      ? props.value !== props.defaultValue
      : props.value !== null && props.value !== undefined;

  const summary = hasSelection ? getCountedLabel(label, 1) : '';

  const [search, setSearch] = useState('');
  const [focused, setFocused] = useState(false);
  const controlledInputValue = focused ? search : summary;

  return (
    <Autocomplete<OptionEntry<T>, false, boolean, false>
      size={size}
      options={normalized}
      value={selected}
      disableClearable={!props.clearable}
      isOptionEqualToValue={(o, v) =>
        o != null && v != null && o.value === v.value
      }
      getOptionLabel={(o) =>
        typeof o === 'string' ? (o as string) : o?.label ?? ''
      }
      getOptionKey={(o) =>
        typeof o === 'string' ? (o as string) : o?.value ?? ''
      }
      {...(showCount
        ? {
            inputValue: controlledInputValue,
            onInputChange: (_e, next, reason) => {
              if (reason === 'input') setSearch(next);
              if (reason === 'reset') setSearch('');
            },
          }
        : {})}
      onChange={(_e, val) => {
        if (props.clearable) {
          props.onChange(val ? val.value : null);
        } else if (val) {
          props.onChange(val.value);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          label={label}
          placeholder={showCount && hasSelection ? '' : placeholder}
          onFocus={showCount ? () => { setFocused(true); } : undefined}
          onBlur={
            showCount ? () => { setFocused(false); setSearch(''); } : undefined
          }
        />
      )}
      sx={{ minWidth }}
    />
  );
};

export default SingleAutocompleteFilter;
